import LegalLayout from "../components/LegalLayout";

const sections = [
  { id: "information", number: "01", label: "Information We Collect" },
  { id: "usage", number: "02", label: "How We Use Information" },
  { id: "payments", number: "03", label: "Payments" },
  { id: "sharing", number: "04", label: "Sharing of Information" },
  { id: "cookies", number: "05", label: "Cookies & Analytics" },
  { id: "retention", number: "06", label: "Data Retention" },
  { id: "security", number: "07", label: "Data Security" },
  { id: "rights", number: "08", label: "Your Privacy Rights" },
  { id: "children", number: "09", label: "Children's Privacy" },
  { id: "third-party", number: "10", label: "Third-Party Services" },
  { id: "changes", number: "11", label: "Policy Updates" },
  { id: "contact", number: "12", label: "Contact Us" },
];

function Section({ id, number, title, children }) {
  return (
    <section id={id} className="legal-section">
      <div className="legal-section-heading">
        <span className="legal-section-number">{number}</span>
        <h2>{title}</h2>
      </div>

      {children}
    </section>
  );
}

function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      description="This Privacy Policy explains what information CODEION collects, why we use it, how we protect it, and the choices available to users of our website and learning services."
      lastUpdated="July 2026"
      sections={sections}
    >
      <div className="legal-intro">
        Your privacy is important to us. This Privacy Policy applies when you
        visit the CODEION website, create an account, contact us, enroll in a
        program, make a payment, or use our learning platform and related
        services.
      </div>

      <Section id="information" number="01" title="Information We Collect">
        <p>
          We may collect information that you provide directly to CODEION and
          information generated when you use our website and learning services.
        </p>

        <h3>Information you provide</h3>

        <ul>
          <li>Name, email address, phone number, and account information.</li>
          <li>Information submitted through registration and enrollment forms.</li>
          <li>Course selections, batch details, assignments, projects, and learning progress.</li>
          <li>Support requests, feedback, enquiries, and communications with CODEION.</li>
          <li>Payment-related references and transaction confirmation information.</li>
        </ul>

        <h3>Information collected automatically</h3>

        <ul>
          <li>Browser type, device information, and operating system.</li>
          <li>IP address and approximate location derived from technical data.</li>
          <li>Pages visited, platform interactions, and usage information.</li>
          <li>Login activity, security logs, and diagnostic information.</li>
        </ul>
      </Section>

      <Section id="usage" number="02" title="How We Use Your Information">
        <p>CODEION may use collected information to:</p>

        <ul>
          <li>Create and manage user accounts.</li>
          <li>Process enrollments and provide access to courses and dashboards.</li>
          <li>Deliver live classes, mentorship, projects, assignments, and learning resources.</li>
          <li>Respond to enquiries and provide customer support.</li>
          <li>Send service-related messages and important platform updates.</li>
          <li>Improve website performance, course delivery, and user experience.</li>
          <li>Detect fraud, misuse, unauthorized access, and security threats.</li>
          <li>Comply with applicable legal and regulatory requirements.</li>
        </ul>
      </Section>

      <Section id="payments" number="03" title="Payment Information">
        <p>
          Payments for CODEION programs may be processed using third-party
          payment service providers.
        </p>

        <p>
          CODEION may receive and store transaction references, payment status,
          order identifiers, enrollment details, and other information required
          for payment verification, support, accounting, and refund processing.
        </p>

        <div className="legal-notice">
          CODEION does not intentionally store complete card details, UPI PINs,
          internet banking passwords, or other payment credentials that are
          entered directly into the payment provider's secure payment interface.
        </div>
      </Section>

      <Section id="sharing" number="04" title="Sharing of Information">
        <p>
          CODEION does not sell users' personal information.
        </p>

        <p>
          Information may be shared with service providers when reasonably
          necessary to operate the website and provide our services.
        </p>

        <ul>
          <li>Cloud hosting and database providers.</li>
          <li>Authentication and account service providers.</li>
          <li>Payment processors and payment verification services.</li>
          <li>Email and communication service providers.</li>
          <li>Analytics, security, and technical infrastructure providers.</li>
          <li>Professional advisers or authorities when required by applicable law.</li>
        </ul>
      </Section>

      <Section id="cookies" number="05" title="Cookies & Analytics">
        <p>
          CODEION may use cookies, local storage, analytics technologies, and
          similar tools to maintain sessions, remember preferences, understand
          website usage, improve performance, and protect the platform.
        </p>

        <p>
          Browser settings may allow you to restrict certain cookies. Disabling
          necessary browser storage or cookies may affect some website features.
        </p>
      </Section>

      <Section id="retention" number="06" title="Data Retention">
        <p>
          We retain personal information for as long as reasonably necessary to
          provide services, maintain enrollment and transaction records, resolve
          disputes, enforce agreements, prevent fraud, and comply with applicable
          legal requirements.
        </p>

        <p>
          Retention periods may differ depending on the type of information and
          the purpose for which it is processed.
        </p>
      </Section>

      <Section id="security" number="07" title="Data Security">
        <p>
          CODEION uses reasonable technical and organizational practices intended
          to protect personal information against unauthorized access, loss,
          misuse, alteration, and disclosure.
        </p>

        <p>
          However, no website, database, internet transmission, or electronic
          storage system can be guaranteed to be completely secure.
        </p>
      </Section>

      <Section id="rights" number="08" title="Your Privacy Rights & Choices">
        <p>
          Depending on applicable law and the circumstances of your request, you
          may contact CODEION to request access to, correction of, or deletion of
          certain personal information associated with your account.
        </p>

        <p>
          We may need to verify your identity before processing privacy-related
          requests. Some information may need to be retained where required for
          legal, security, fraud-prevention, accounting, or legitimate operational
          purposes.
        </p>
      </Section>

      <Section id="children" number="09" title="Children's Privacy">
        <p>
          CODEION services are intended for learners who can lawfully use the
          services or who use them with appropriate involvement and authorization
          from a parent or guardian where required by applicable law.
        </p>

        <p>
          If you believe personal information relating to a child has been
          submitted improperly, please contact our support team.
        </p>
      </Section>

      <Section id="third-party" number="10" title="Third-Party Services & Links">
        <p>
          Our website and learning services may use or link to independent
          third-party services. Those providers operate under their own terms and
          privacy policies.
        </p>

        <p>
          We recommend reviewing the privacy practices of third-party services
          before providing information directly to them.
        </p>
      </Section>

      <Section id="changes" number="11" title="Changes to This Privacy Policy">
        <p>
          CODEION may update this Privacy Policy to reflect changes in our
          services, technology, operational practices, or legal requirements.
        </p>

        <p>
          The latest version will be published on this page with a revised
          "Last updated" date.
        </p>
      </Section>

      <Section id="contact" number="12" title="Contact Us">
        <p>
          For privacy questions, correction requests, deletion requests, or
          concerns about how CODEION handles personal information, contact:
        </p>

        <p>
          Email:{" "}
          <a href="mailto:support@codeion.in">
            support@codeion.in
          </a>
        </p>

        <p>
          Support hours: Monday to Saturday, 10:00 AM to 7:00 PM IST.
        </p>
      </Section>

      <div className="legal-contact-card">
        <h2>Have a privacy question?</h2>

        <p>
          Contact the CODEION support team if you have questions about your
          personal information, account data, or a privacy-related request.
        </p>

        <a
          href="mailto:support@codeion.in"
          className="legal-contact-button"
        >
          Contact Support →
        </a>
      </div>
    </LegalLayout>
  );
}

export default Privacy;